import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable, filter, firstValueFrom, takeUntil, tap } from 'rxjs';

import { ItineraryData } from '@hiptraveler/data-access/api';
import { SearchAction, SearchState } from '@hiptraveler/data-access/search';
import { LeafletMapControlStateService } from '@hiptraveler/features/leaflet-map';
import { AppListenerService, SearchLocationData, SearchLocationService, WINDOW, queryStringToObject, cleanObject, brandCampaignId, RequestCancellationService, brandSite } from '@hiptraveler/common';
import { DataAccess, DataService, StateOption } from './data.service';

@Injectable()
export class ItinerariesDataService extends DataService implements DataAccess {

  @Select(SearchState.itineraries) itineraries$: Observable<ItineraryData[]>;

  constructor(
    @Inject(WINDOW) private window: any,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private requestCancellation: RequestCancellationService,
    appListener: AppListenerService,
    searchLocation: SearchLocationService,
    leafletControl: LeafletMapControlStateService
  ) {
    super(appListener, searchLocation, leafletControl);
  }

  ngOnDestroy(): void {
    this.subscription$.next();
  }

  get viewDisplayItems$(): Observable<ItineraryData[]> {
    return this.getViewDisplayItems(this.store.select(SearchState.itineraries));
  }

  get searchResults$(): Observable<ItineraryData[] | null> {
    return this.getSearchResultData(this.store.select(SearchState.itineraries));
  }

  async initializeDataAccess(): Promise<void> {
    await this.getBrandCampaign();
    this.getResultsByLocationData({ default: true });
    this.locationChanges(this);
    this.searchResultLocations(this.itineraries$, 'itinerary');
    this.observeLoadMoreEventClicks();
  }

  async getResultsByLocationData(value?: StateOption): Promise<void> {
    if (!this.searchPage) {
      await this.resetSearchState(this, [ 'pagination' ]);
    }
    if (value?.default) {
      return brandSite()
        ? this.getBrandContentByData(1)
        : this.getSearchResultsByLocation(this.searchLocation.data);
    }
    this.getSearchResultsByLocation(this.searchLocation.data);
  }

  getSearchResultsByLocation(data: SearchLocationData | null): void {
    if (brandSite() && !data?.locId) return this.getBrandContentByData(1);
    if (!this.searchPage) this.requestCancellation.cancelAllSearchRequests()

    this.dispatchActionRequest(async () => {
      await firstValueFrom(this.store.dispatch(new SearchAction.GetItinerariesByQuery({
        q: 'most-hip', ...cleanObject(data), ...queryStringToObject(this.window.location.search)
      })));
      this.appListener.loadMoreEventState$$.next(false);
    });
  }

  getBrandContentByData(page: number): void {
    if (this.searchLocation.data?.locId) return this.getSearchResultsByLocation(this.searchLocation.data);

    this.dispatchActionRequest(async () => {
      await firstValueFrom(this.store.dispatch(new SearchAction.GetItinerariesContent({
        cId: brandCampaignId(), brand: 'cItineraries', page
      })));
      this.appListener.loadMoreEventState$$.next(false);
    });
  }

  observeLoadMoreEventClicks(): void { /* Observer State Changes Methods */
    this.appListener.loadMoreEventState$.pipe(
      filter(Boolean),
      tap(() => this.observeLoadMoreEvent(this.route, this.getBrandContentByData.bind(this), this.getSearchResultsByLocation.bind(this))),
      takeUntil(this.subscription$)
    ).subscribe();
  }

}
